import PropTypes from "prop-types";
import React, { useState, useRef } from "react";
import Button from "react-bootstrap/Button";

import Base64ImageCropper from "./Base64ImageCropper.jsx";

const ImageUploader = ({
  buttonText = "Upload image",
  cropperProps = { aspectRatio: 1 },
  onFileReady,
  removeBase64DataUriPrefix = false,
  useCropper = false,
}) => {
  const fileInputRef = useRef(null);
  const [imageData, setImageData] = useState("");
  const [showImageCropper, setShowImageCropper] = useState(false);

  // Utility to remove the data URI prefix if needed.
  const removePrefix = (data) =>
    data.replace(/^data:image\/(png|jpg|jpeg);base64,/, "");

  // Convert file to base64 using FileReader.
  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const result = removeBase64DataUriPrefix
          ? removePrefix(reader.result)
          : reader.result;
        resolve(result);
      };
      reader.onerror = (error) => reject(error);
    });

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await toBase64(file);
      if (useCropper) {
        setImageData(data);
        setShowImageCropper(true);
      } else {
        onFileReady(data);
      }
    } catch (error) {
      console.error("Error converting file to base64:", error);
    }
  };

  const handleCropSave = (croppedData) => {
    setShowImageCropper(false);
    const finalData = removeBase64DataUriPrefix
      ? removePrefix(croppedData)
      : croppedData;
    onFileReady(finalData);
  };

  return (
    <div>
      <Button
        variant="primary"
        onClick={() => fileInputRef.current && fileInputRef.current.click()}
      >
        {buttonText}
      </Button>
      <input
        ref={fileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleFileChange}
      />
      {showImageCropper && (
        <Base64ImageCropper
          data={`data:image/png;base64,${removePrefix(imageData)}`}
          onHide={() => setShowImageCropper(false)}
          onSave={handleCropSave}
          {...cropperProps}
        />
      )}
    </div>
  );
};

ImageUploader.propTypes = {
  buttonText: PropTypes.string,
  cropperProps: PropTypes.shape({
    aspectRatio: PropTypes.number,
    scale: PropTypes.arrayOf(PropTypes.number),
  }),
  onFileReady: PropTypes.func.isRequired,
  removeBase64DataUriPrefix: PropTypes.bool,
  useCropper: PropTypes.bool,
};

export default ImageUploader;
